import { PrimitiveAutocomplete } from "@src/components/fields/PrimitiveAutocomplete";
import { countries, type Country } from "countries-list";
import { type FunctionComponent, type PropsWithChildren, useMemo } from "react";
import { type FieldRenderProps } from "react-final-form";

interface CountryOption {
    label: string;
    value: string;
}

function getCountries(): [string, Country][] {
    return Object.entries(countries).filter(([, { continent }]) => continent === "EU");
}

export function getCountryName(countryKey: string): string | undefined {
    const countries = getCountries();
    const result = countries.find(([key]) => key === countryKey);

    if (result) {
        const [, country] = result;

        return country.name;
    }
}

export function getCountryListLong(): CountryOption[] {
    const countries = getCountries();

    return countries.map(([value, { emoji, name }]) => ({ label: `${emoji} ${name}`, value }));
}

function getCountryListShort(): CountryOption[] {
    const countries = getCountries();

    return countries.map(([value]) => ({ label: value, value }));
}

interface SelectProps extends FieldRenderProps<string, HTMLInputElement | HTMLTextAreaElement> {
    labelVariant?: "short" | "long";
}

const CountrySelect: FunctionComponent<PropsWithChildren<SelectProps>> = ({ labelVariant = "short", ...props }) => {
    const optionList = useMemo(() => (labelVariant === "short" ? getCountryListShort() : getCountryListLong()), [labelVariant]);
    const options = useMemo(() => optionList.map((option) => option.value), [optionList]);
    const labelByValue = useMemo(() => Object.fromEntries(optionList.map((option) => [option.value, option.label])), [optionList]);

    return (
        <PrimitiveAutocomplete
            {...props}
            options={options}
            // The autocomplete never asks for a label for the empty/no-selection value (it renders an empty
            // input), so — unlike the old FinalFormSelect — there's no placeholder-via-label case to defer to
            // the caller for. A real selection always renders this component's own label (e.g. "🇦🇹 Österreich").
            getOptionLabel={(value) => labelByValue[value] ?? String(value)}
        />
    );
};

export default CountrySelect;
